import { signal } from "@preact/signals-core";
import { ApiRequestError } from "../lib/api";
import { bindErrorText, bindValue, html } from "../lib/bind";
import { bindNullableValue, bindSelect } from "../lib/formBind";
import { form } from "../lib/form";
import { parseMoneyInput } from "../lib/money";
import { currentScope } from "../lib/scope";
import { toast } from "../lib/toast";
import { type Client, getClient } from "../services/client";
import {
  type Project,
  type ProjectInput,
  deactivateProject,
  getProject,
  updateProject,
} from "../services/project";
import { breadcrumb } from "../stores/breadcrumb";
import { buildTimesheetPanel } from "./timesheets";

function toProjectInput(project: Project): ProjectInput {
  return {
    name: project.name,
    code: project.code,
    rate: project.rate,
    rate_unit: project.rate_unit,
    currency: project.currency,
    active: project.active,
    notes: project.notes,
    hours_per_day: project.hours_per_day,
    days_per_week: project.days_per_week,
    days_per_month: project.days_per_month,
  };
}

export function projectDetailView(params: Record<string, string>): Node {
  const projectId = params.id ?? "";
  const project = signal<Project | null>(null);
  const savedAt = signal("");
  const errorMessage = signal("");

  breadcrumb.value = { label: "Clients", path: "/clients" };
  currentScope().cleanup(() => {
    breadcrumb.value = null;
  });

  const projectForm = form<ProjectInput>({
    name: "",
    code: null,
    rate: "0.00",
    rate_unit: "hour",
    currency: null,
    active: true,
    notes: null,
    hours_per_day: "8.00",
    days_per_week: "5.00",
    days_per_month: "20.00",
  });

  getProject(projectId)
    .then((loaded) => {
      project.value = loaded;
      projectForm.reset(toProjectInput(loaded));
      savedAt.value = loaded.updated_at;
      return getClient(loaded.client_id);
    })
    .then((client: Client) => {
      breadcrumb.value = { label: client.name, path: `/clients/${client.id}` };
    })
    .catch((err: unknown) => {
      errorMessage.value = err instanceof ApiRequestError ? err.body.message : "Failed to load";
    });

  async function save(event: Event): Promise<void> {
    event.preventDefault();
    errorMessage.value = "";
    try {
      const updated = await updateProject(projectId, {
        ...projectForm.values(),
        updated_at: savedAt.value,
      });
      project.value = updated;
      savedAt.value = updated.updated_at;
      projectForm.reset(toProjectInput(updated));
      toast.success("Project saved.");
    } catch (err) {
      if (err instanceof ApiRequestError && err.status === 412) {
        errorMessage.value = "This project changed elsewhere — reload and retry.";
      } else {
        errorMessage.value = err instanceof ApiRequestError ? err.body.message : "Save failed";
      }
      toast.error(errorMessage.value);
    }
  }

  async function toggleActive(): Promise<void> {
    if (!project.value) return;
    try {
      const updated = project.value.active
        ? await deactivateProject(projectId)
        : await updateProject(projectId, { ...projectForm.values(), active: true, updated_at: savedAt.value });
      project.value = updated;
      savedAt.value = updated.updated_at;
      projectForm.reset(toProjectInput(updated));
      toast.success(updated.active ? "Project reactivated." : "Project deactivated.");
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Update failed");
    }
  }

  const nameText = () => project.value?.name ?? "Project";

  const fragment = html`<div class="view">
    <div class="page-header">
      <h1 class="project-title">${nameText()}</h1>
      <button class="btn toggle-active-btn" type="button"></button>
    </div>
    <p class="form-error"></p>

    <form class="form project-form" onsubmit="${save}">
      <div class="form-row">
        <div class="form-field">
          <label for="pr-name">Name</label>
          <input id="pr-name" required />
        </div>
        <div class="form-field">
          <label for="pr-code">Code</label>
          <input id="pr-code" />
        </div>
        <div class="form-field">
          <label for="pr-rate">Rate</label>
          <input id="pr-rate" inputmode="decimal" />
        </div>
        <div class="form-field">
          <label for="pr-rate-unit">Per</label>
          <select id="pr-rate-unit">
            <option value="hour">Hour</option>
            <option value="day">Day</option>
            <option value="fixed">Fixed</option>
            <option value="item">Item</option>
          </select>
        </div>
        <div class="form-field">
          <label for="pr-currency">Currency override</label>
          <input id="pr-currency" placeholder="Client default" />
        </div>
      </div>

      <div class="card-label">Timesheet conversion</div>
      <p class="field-hint">
        Only used for hour/day-rate projects, converting logged time into the project's
        billing unit (05 § Timesheets).
      </p>
      <div class="form-row">
        <div class="form-field">
          <label for="pr-hours-per-day">Hours / day</label>
          <input id="pr-hours-per-day" inputmode="decimal" />
        </div>
        <div class="form-field">
          <label for="pr-days-per-week">Days / week</label>
          <input id="pr-days-per-week" inputmode="decimal" />
        </div>
        <div class="form-field">
          <label for="pr-days-per-month">Days / month</label>
          <input id="pr-days-per-month" inputmode="decimal" />
        </div>
      </div>

      <div class="form-field">
        <label for="pr-notes">Notes</label>
        <textarea id="pr-notes" rows="3"></textarea>
      </div>

      <div class="form-actions">
        <button class="btn btn-primary" type="submit">Save</button>
      </div>
    </form>

    <div class="card">
      <div class="card-label">Timesheet entries</div>
      <div class="project-timesheet-slice"></div>
    </div>
  </div>`;

  const errorEl = fragment.querySelector<HTMLParagraphElement>(".form-error");
  const titleEl = fragment.querySelector<HTMLElement>(".project-title");
  const toggleBtn = fragment.querySelector<HTMLButtonElement>(".toggle-active-btn");
  const nameInput = fragment.querySelector<HTMLInputElement>("#pr-name");
  const codeInput = fragment.querySelector<HTMLInputElement>("#pr-code");
  const rateInput = fragment.querySelector<HTMLInputElement>("#pr-rate");
  const rateUnitSelect = fragment.querySelector<HTMLSelectElement>("#pr-rate-unit");
  const currencyInput = fragment.querySelector<HTMLInputElement>("#pr-currency");
  const hoursPerDayInput = fragment.querySelector<HTMLInputElement>("#pr-hours-per-day");
  const daysPerWeekInput = fragment.querySelector<HTMLInputElement>("#pr-days-per-week");
  const daysPerMonthInput = fragment.querySelector<HTMLInputElement>("#pr-days-per-month");
  const notesInput = fragment.querySelector<HTMLTextAreaElement>("#pr-notes");
  const timesheetSlice = fragment.querySelector<HTMLDivElement>(".project-timesheet-slice");

  if (
    !errorEl ||
    !titleEl ||
    !toggleBtn ||
    !nameInput ||
    !codeInput ||
    !rateInput ||
    !rateUnitSelect ||
    !currencyInput ||
    !hoursPerDayInput ||
    !daysPerWeekInput ||
    !daysPerMonthInput ||
    !notesInput ||
    !timesheetSlice
  ) {
    throw new Error("project detail markup missing expected elements");
  }

  bindErrorText(errorEl, errorMessage);
  currentScope().effect(() => {
    titleEl.textContent = nameText();
    toggleBtn.textContent = project.value?.active === false ? "Reactivate" : "Deactivate";
    toggleBtn.style.display = project.value ? "" : "none";
  });
  toggleBtn.addEventListener("click", () => {
    void toggleActive();
  });

  bindValue(nameInput, projectForm.fields.name);
  bindNullableValue(codeInput, projectForm.fields.code);
  bindValue(rateInput, projectForm.fields.rate, {
    format: (v) => v,
    parse: (v) => parseMoneyInput(v) ?? v,
  });
  bindSelect(rateUnitSelect, projectForm.fields.rate_unit);
  bindNullableValue(currencyInput, projectForm.fields.currency);
  bindValue(hoursPerDayInput, projectForm.fields.hours_per_day, {
    format: (v) => v,
    parse: (v) => parseMoneyInput(v) ?? v,
  });
  bindValue(daysPerWeekInput, projectForm.fields.days_per_week, {
    format: (v) => v,
    parse: (v) => parseMoneyInput(v) ?? v,
  });
  bindValue(daysPerMonthInput, projectForm.fields.days_per_month, {
    format: (v) => v,
    parse: (v) => parseMoneyInput(v) ?? v,
  });
  bindNullableValue(notesInput, projectForm.fields.notes);

  timesheetSlice.appendChild(buildTimesheetPanel({ fixedProjectId: projectId, showHeader: false }));

  return fragment;
}
